import { Search, WalletCards, X } from "lucide-react";
import { type RefObject, useMemo, useState } from "react";

import type { CashierWalletView } from "./types";

const LOCAL_CURRENCY_ICONS: Readonly<Record<string, string>> = {
  SOL: "/app/static/themes/monkeytilt/icons/currencies/sol.svg",
  USD: "/app/static/themes/monkeytilt/icons/currencies/usd.svg",
  USDC: "/app/static/themes/monkeytilt/icons/currencies/usdc.svg",
};

/** Brand chip colors for coins without a local icon asset (letter chips). */
const CURRENCY_CHIP_COLORS: Readonly<Record<string, string>> = {
  BTC: "#f7931a",
  ETH: "#627eea",
  LTC: "#bfbbbb",
  DOGE: "#c2a633",
  BCH: "#8dc351",
  BNB: "#f3ba2f",
  TRX: "#eb0029",
  XRP: "#23292f",
  SHIB: "#ffa409",
  USDT: "#26a17b",
  POL: "#8247e5",
  MATIC: "#8247e5",
  TON: "#0098ea",
  ADA: "#0033ad",
  EUR: "#2775ca",
  GBP: "#b4527a",
  CAD: "#c94b32",
};

export function walletCurrencyIcon(wallet: CashierWalletView): string | null {
  return (
    wallet.iconUrl ??
    LOCAL_CURRENCY_ICONS[wallet.currency.toUpperCase()] ??
    null
  );
}

type WalletMenuProps = {
  busy: boolean;
  message: string | null;
  onClose: () => void;
  onOpenCashier: () => void;
  onSelectWallet: (walletId: string) => Promise<void>;
  panelRef: RefObject<HTMLDivElement | null>;
  selectedWallet: CashierWalletView | null;
  totalBalance: string | null;
  wallets: readonly CashierWalletView[];
};

function CurrencyIcon({ wallet }: { wallet: CashierWalletView }) {
  const source = walletCurrencyIcon(wallet);
  const chipColor = CURRENCY_CHIP_COLORS[wallet.currency.toUpperCase()];

  return (
    <span
      className="cashier-wallet-menu__wallet-icon"
      aria-hidden
      style={!source && chipColor ? { background: chipColor } : undefined}
    >
      {source ? (
        <img alt="" src={source} />
      ) : (
        <span>{Array.from(wallet.currency)[0]?.toUpperCase() ?? "?"}</span>
      )}
    </span>
  );
}

function WalletRow({
  busy,
  onSelect,
  selected,
  wallet,
}: {
  busy: boolean;
  onSelect: () => void;
  selected: boolean;
  wallet: CashierWalletView;
}) {
  const content = (
    <>
      <CurrencyIcon wallet={wallet} />
      <strong className="cashier-wallet-menu__wallet-copy">
        {wallet.currency}
      </strong>
      <span className="cashier-wallet-menu__wallet-amount">{wallet.total}</span>
    </>
  );

  if (wallet.readOnly) {
    return (
      <div
        aria-selected={selected}
        className="cashier-wallet-menu__wallet is-read-only"
        role="option"
      >
        {content}
      </div>
    );
  }

  return (
    <button
      aria-selected={selected}
      className="cashier-wallet-menu__wallet"
      data-wallet-item
      disabled={busy}
      onClick={onSelect}
      role="option"
      type="button"
    >
      {content}
    </button>
  );
}

export function WalletMenu({
  busy,
  message,
  onClose,
  onOpenCashier,
  onSelectWallet,
  panelRef,
  selectedWallet,
  totalBalance,
  wallets,
}: WalletMenuProps) {
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredWallets = useMemo(
    () =>
      wallets.filter((wallet) =>
        `${wallet.currency} ${wallet.label}`
          .toLocaleLowerCase()
          .includes(normalizedSearch),
      ),
    [normalizedSearch, wallets],
  );
  const total = totalBalance
    ? `${selectedWallet?.stale ? "~" : ""}${totalBalance}`
    : "Unavailable";

  return (
    <div
      aria-label="Wallet balances"
      className="cashier-wallet-menu"
      id="cashier-wallet-menu"
      ref={panelRef}
      role="dialog"
      tabIndex={-1}
    >
      <div className="cashier-wallet-menu__handle" aria-hidden />
      <div className="cashier-wallet-menu__scroll">
        <div className="cashier-wallet-menu__header">
          <span>Total Balance</span>
          <strong aria-live="polite">{total}</strong>
        </div>

        <label className="cashier-wallet-menu__search">
          <span className="sr-only">Search currencies</span>
          <Search aria-hidden />
          <input
            autoComplete="off"
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search Currencies"
            type="search"
            value={search}
          />
          {search ? (
            <button
              aria-label="Clear currency search"
              onClick={() => setSearch("")}
              type="button"
            >
              <X aria-hidden />
            </button>
          ) : null}
        </label>

        <div
          aria-label="Available currencies"
          className="cashier-wallet-menu__list"
          role="listbox"
        >
          {filteredWallets.map((wallet) => (
            <WalletRow
              busy={busy}
              key={wallet.id}
              onSelect={() => {
                void onSelectWallet(wallet.id).then(onClose);
              }}
              selected={wallet.id === selectedWallet?.id}
              wallet={wallet}
            />
          ))}
          {!filteredWallets.length ? (
            <p className="cashier-wallet-menu__empty">
              {wallets.length
                ? "No matching currencies"
                : (message ?? "Balance unavailable")}
            </p>
          ) : null}
        </div>

        <div className="cashier-wallet-menu__footer">
          <button onClick={onOpenCashier} type="button">
            <WalletCards aria-hidden />
            <span>Open Cashier</span>
          </button>
        </div>
      </div>
    </div>
  );
}
